import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { AlertTriangle, Trash2, X } from "lucide-react";

const DELETE_CONFIRM_PHRASE = "KONTO LOESCHEN";

export default function PortalAccount() {
  const [me, setMe] = useState(null);
  const [form, setForm] = useState({});
  const [pw, setPw] = useState({ current: "", new1: "", new2: "" });
  // Zweistufige Konto-Löschung: 0 = Button, 1 = Warnung, 2 = Passwort + Phrase.
  const [delStep, setDelStep] = useState(0);
  const [delForm, setDelForm] = useState({ password: "", phrase: "" });
  const [delBusy, setDelBusy] = useState(false);
  // Newsletter-Consent (Reaktivierung/Widerruf) — inkl. Historie.
  const [nlProfile, setNlProfile] = useState(null);
  const [nlBusy, setNlBusy] = useState(false);

  async function loadNewsletterProfile() {
    try {
      const r = await api.get("/customer/newsletter/profile");
      setNlProfile(r.data);
    } catch (e) {
      // Not-Login-Zustand oder Netzwerkfehler — Panel wird ausgeblendet.
      setNlProfile(null);
    }
  }

  useEffect(() => {
    api.get("/customer/me").then((r) => {
      setMe(r.data);
      setForm({
        first_name: r.data.first_name, last_name: r.data.last_name,
        address_street: r.data.address_street || "",
        address_zip: r.data.address_zip || "",
        address_city: r.data.address_city || "",
        address_country: r.data.address_country || "",
        date_of_birth: r.data.date_of_birth || "",
      });
    });
    loadNewsletterProfile();
  }, []);

  async function toggleMarketingOptIn(next) {
    setNlBusy(true);
    try {
      const r = await api.put("/customer/newsletter/profile",
        { marketing_opt_in: next });
      setNlProfile(r.data);
      toast.success(next
        ? "Newsletter abonniert. Willkommen zurück!"
        : "Vom Newsletter abgemeldet.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Speichern fehlgeschlagen");
    } finally {
      setNlBusy(false);
    }
  }

  async function save(e) {
    e.preventDefault();
    try {
      const payload = {};
      Object.entries(form).forEach(([k, v]) => { if (v !== "") payload[k] = v; });
      const r = await api.patch("/customer/me", payload);
      setMe(r.data);
      toast.success("Profil gespeichert");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Speichern fehlgeschlagen");
    }
  }

  async function changePw(e) {
    e.preventDefault();
    if (pw.new1 !== pw.new2) { toast.error("Passwörter stimmen nicht überein"); return; }
    if (pw.new1.length < 12) { toast.error("Mindestens 12 Zeichen"); return; }
    try {
      await api.post("/customer/change-password", { current_password: pw.current, new_password: pw.new1 });
      toast.success("Passwort geändert. Bitte neu anmelden.");
      setTimeout(() => { window.location.href = "/login"; }, 1200);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Änderung fehlgeschlagen");
    }
  }

  function closeDelete() {
    setDelStep(0);
    setDelForm({ password: "", phrase: "" });
  }

  async function confirmDelete() {
    if (delForm.phrase.trim().toUpperCase() !== DELETE_CONFIRM_PHRASE) {
      toast.error(`Bestätigungstext muss exakt "${DELETE_CONFIRM_PHRASE}" lauten.`);
      return;
    }
    setDelBusy(true);
    try {
      await api.post("/customer/delete-account", {
        confirm_phrase: DELETE_CONFIRM_PHRASE,
        current_password: delForm.password,
      });
      toast.success("Konto gelöscht. Du wirst abgemeldet.");
      setTimeout(() => { window.location.href = "/login"; }, 1400);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Löschung fehlgeschlagen");
    } finally {
      setDelBusy(false);
    }
  }

  if (!me) return <div className="text-zinc-500">Lade…</div>;
  return (
    <div className="space-y-8" data-testid="portal-account">
      <div>
        <div className="text-3xl font-black uppercase tracking-tight">Mein Konto</div>
        <div className="text-sm text-zinc-500">Persönliche Daten und Passwort verwalten.</div>
      </div>

      <form onSubmit={save} className="border border-zinc-800 rounded-lg p-5 space-y-4 bg-zinc-900/70">
        <div className="text-xs uppercase tracking-wider text-cyan-400">Persönliche Daten</div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Vorname <span className="text-cyan-400">*</span></Label><Input required data-testid="account-first-name" value={form.first_name} onChange={(e) => setForm({...form, first_name: e.target.value})} /></div>
          <div><Label>Nachname <span className="text-cyan-400">*</span></Label><Input required data-testid="account-last-name" value={form.last_name} onChange={(e) => setForm({...form, last_name: e.target.value})} /></div>
        </div>
        <div><Label>E-Mail <span className="text-cyan-400">*</span></Label><Input disabled value={me.email} data-testid="account-email" /></div>
        <div>
          <Label>Geburtsdatum <span className="text-xs text-zinc-500">(für Altersfreigaben)</span></Label>
          <Input type="date" data-testid="account-date-of-birth"
            value={form.date_of_birth || ""}
            onChange={(e) => setForm({ ...form, date_of_birth: e.target.value })} />
          <div className="text-[11px] text-zinc-500 mt-1">
            Wird ausschließlich für Altersfreigaben und die Ab-18-Newsletter-Zielgruppe genutzt.
          </div>
        </div>
        <div className="text-[11px] text-zinc-500 border-l-2 border-cyan-500/40 pl-2 leading-relaxed" data-testid="account-privacy-hint">
          <span className="text-cyan-400">*</span> Pflichtfeld. Weitere Angaben (Anschrift) sind
          freiwillig. Details zur Verarbeitung in der{" "}
          <a href="/portal/datenschutz" className="text-cyan-400 hover:underline">Datenschutzerklärung</a>.
        </div>
        <div><Label>Straße</Label><Input data-testid="account-street" value={form.address_street} onChange={(e) => setForm({...form, address_street: e.target.value})} /></div>
        <div className="grid grid-cols-[1fr_2fr_5rem] gap-3">
          <div><Label>PLZ</Label><Input data-testid="account-zip" value={form.address_zip} onChange={(e) => setForm({...form, address_zip: e.target.value})} /></div>
          <div><Label>Ort</Label><Input data-testid="account-city" value={form.address_city} onChange={(e) => setForm({...form, address_city: e.target.value})} /></div>
          <div><Label>Land</Label><Input maxLength={2} data-testid="account-country" value={form.address_country} onChange={(e) => setForm({...form, address_country: e.target.value.toUpperCase()})} /></div>
        </div>
        <Button type="submit" data-testid="account-save" className="rounded-sm">Speichern</Button>
      </form>

      {nlProfile !== null && (
        <div
          className="border border-zinc-800 rounded-lg p-5 space-y-3 bg-zinc-900/70"
          data-testid="account-newsletter-panel"
        >
          <div className="text-xs uppercase tracking-wider text-cyan-400">Newsletter & Angebote</div>
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="text-sm text-zinc-300 leading-relaxed max-w-md">
              {nlProfile.marketing_opt_in ? (
                <span data-testid="account-newsletter-state-on">
                  Du erhältst aktuell unseren Newsletter mit Angeboten und Aktionen.
                </span>
              ) : (
                <span data-testid="account-newsletter-state-off">
                  Du erhältst aktuell KEINEN Newsletter. Du kannst dich hier
                  jederzeit wieder anmelden.
                </span>
              )}
            </div>
            <Button
              type="button"
              data-testid="account-newsletter-toggle"
              disabled={nlBusy}
              onClick={() => toggleMarketingOptIn(!nlProfile.marketing_opt_in)}
              className={nlProfile.marketing_opt_in
                ? "bg-zinc-800 hover:bg-zinc-700 text-zinc-100 rounded-sm"
                : "bg-cyan-400 hover:bg-cyan-300 text-zinc-950 rounded-sm"}
            >
              {nlBusy ? "…" : nlProfile.marketing_opt_in ? "Abmelden" : "Anmelden"}
            </Button>
          </div>
          <div className="text-[11px] text-zinc-500 grid sm:grid-cols-2 gap-x-6 gap-y-1">
            <div data-testid="account-newsletter-given-at">
              Zuletzt angemeldet am:{" "}
              <span className="text-zinc-300">
                {nlProfile.marketing_consent_given_at
                  ? new Date(nlProfile.marketing_consent_given_at).toLocaleString("de-DE")
                  : "–"}
              </span>
            </div>
            <div data-testid="account-newsletter-revoked-at">
              Zuletzt abgemeldet am:{" "}
              <span className="text-zinc-300">
                {nlProfile.marketing_consent_revoked_at
                  ? new Date(nlProfile.marketing_consent_revoked_at).toLocaleString("de-DE")
                  : "–"}
              </span>
            </div>
          </div>
        </div>
      )}

      <form onSubmit={changePw} className="border border-zinc-800 rounded-lg p-5 space-y-4 bg-zinc-900/70">
        <div className="text-xs uppercase tracking-wider text-cyan-400">Passwort ändern</div>
        <div><Label>Aktuelles Passwort</Label><Input required type="password" data-testid="account-cur-pw" value={pw.current} onChange={(e) => setPw({...pw, current: e.target.value})} /></div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Neues Passwort <span className="text-xs text-zinc-500">(≥12)</span></Label><Input required type="password" minLength={12} data-testid="account-new-pw" value={pw.new1} onChange={(e) => setPw({...pw, new1: e.target.value})} /></div>
          <div><Label>Wiederholen</Label><Input required type="password" minLength={12} data-testid="account-new-pw2" value={pw.new2} onChange={(e) => setPw({...pw, new2: e.target.value})} /></div>
        </div>
        <Button type="submit" data-testid="account-change-pw" className="rounded-sm">Passwort speichern</Button>
      </form>

      {/* --- Danger Zone: Konto löschen ---------------------------------
          Zweistufige Bestätigung + Server-side Passwort + Phrase-Prüfung.
          Verkaufsbelege bleiben aus gesetzlichen Gründen anonymisiert
          erhalten (§ 147 AO / § 257 HGB) — nur PII wird entwertet. */}
      <div className="border border-rose-500/40 rounded-lg p-5 space-y-3 bg-rose-500/5" data-testid="account-danger-zone">
        <div className="text-xs uppercase tracking-wider text-rose-400 flex items-center gap-2">
          <AlertTriangle size={14} /> Mein Konto löschen
        </div>
        <div className="text-sm text-zinc-300 leading-relaxed">
          Dein Konto und alle personenbezogenen Daten werden entfernt. Aus
          gesetzlichen Aufbewahrungspflichten bleiben Verkaufs- und
          Kassenbelege anonymisiert für die Buchhaltung bestehen.
        </div>
        <Button
          type="button"
          data-testid="account-delete-btn"
          onClick={() => setDelStep(1)}
          className="bg-rose-600 hover:bg-rose-500 text-white rounded-sm border border-rose-400"
        >
          <Trash2 size={14} className="mr-2 shrink-0" /> Mein Konto löschen
        </Button>
      </div>

      {delStep > 0 && (
        <div
          data-testid="account-delete-modal"
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
        >
          <div className="w-full max-w-md bg-zinc-950 border border-rose-500/60 rounded-lg shadow-2xl relative">
            <button
              type="button"
              data-testid="account-delete-close"
              onClick={closeDelete}
              aria-label="Schließen"
              className="absolute top-3 right-3 w-9 h-9 rounded-full hover:bg-zinc-800 flex items-center justify-center text-zinc-400"
            >
              <X size={18} />
            </button>
            {delStep === 1 && (
              <div className="p-6 space-y-4">
                <div className="flex items-center gap-2 text-rose-400 text-sm uppercase tracking-wider">
                  <AlertTriangle size={16} /> Konto löschen – Schritt 1 / 2
                </div>
                <div className="text-sm text-zinc-200 leading-relaxed space-y-2">
                  <p><strong className="text-rose-300">Das kann nicht rückgängig gemacht werden.</strong></p>
                  <p>Beim Löschen werden folgende Daten sofort entfernt bzw. anonymisiert:</p>
                  <ul className="list-disc pl-5 text-zinc-300 space-y-1">
                    <li>Login-Zugang (E-Mail, Passwort)</li>
                    <li>Name, Anschrift, Kontaktdaten</li>
                    <li>Loyalty-Kartennummer, Newsletter-Präferenz</li>
                  </ul>
                  <p>Aus gesetzlichen Gründen erhalten bleiben (anonymisiert):</p>
                  <ul className="list-disc pl-5 text-zinc-300 space-y-1">
                    <li>Kassenbelege und Rechnungssummen</li>
                    <li>Loyalty-Punkte-Buchungen (Ledger)</li>
                  </ul>
                </div>
                <div className="flex gap-2 pt-2 flex-wrap">
                  <Button type="button" onClick={closeDelete} variant="outline" data-testid="account-delete-cancel-1" className="border-zinc-700">
                    Abbrechen
                  </Button>
                  <Button
                    type="button"
                    onClick={() => setDelStep(2)}
                    data-testid="account-delete-next"
                    className="bg-rose-600 hover:bg-rose-500 text-white rounded-sm"
                  >
                    Ich habe verstanden, weiter
                  </Button>
                </div>
              </div>
            )}
            {delStep === 2 && (
              <div className="p-6 space-y-4">
                <div className="flex items-center gap-2 text-rose-400 text-sm uppercase tracking-wider">
                  <AlertTriangle size={16} /> Konto löschen – Schritt 2 / 2
                </div>
                <div className="text-sm text-zinc-300">
                  Zur endgültigen Bestätigung dein aktuelles Passwort eingeben und
                  <span className="mx-1 font-mono text-rose-300">{DELETE_CONFIRM_PHRASE}</span>
                  in das Feld tippen.
                </div>
                <div className="space-y-3">
                  <div>
                    <Label>Aktuelles Passwort</Label>
                    <Input
                      required
                      type="password"
                      data-testid="account-delete-password"
                      value={delForm.password}
                      onChange={(e) => setDelForm({ ...delForm, password: e.target.value })}
                      className="bg-zinc-900 border-zinc-700"
                    />
                  </div>
                  <div>
                    <Label>Bestätigungstext</Label>
                    <Input
                      required
                      data-testid="account-delete-phrase"
                      value={delForm.phrase}
                      onChange={(e) => setDelForm({ ...delForm, phrase: e.target.value })}
                      placeholder={DELETE_CONFIRM_PHRASE}
                      className="bg-zinc-900 border-zinc-700 font-mono tracking-wider"
                    />
                  </div>
                </div>
                <div className="flex gap-2 pt-2 flex-wrap">
                  <Button type="button" onClick={closeDelete} variant="outline" data-testid="account-delete-cancel-2" className="border-zinc-700">
                    Abbrechen
                  </Button>
                  <Button
                    type="button"
                    onClick={confirmDelete}
                    disabled={delBusy}
                    data-testid="account-delete-confirm"
                    className="bg-rose-600 hover:bg-rose-500 text-white rounded-sm"
                  >
                    <Trash2 size={14} className="mr-2 shrink-0" />
                    {delBusy ? "Lösche…" : "Konto endgültig löschen"}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
